// Today body sections — 1:1 web port of mobile/src/components/today/sections.tsx.
// Same section set, order, brand tokens, typography, badges, pills and micro-
// interactions (spring entrance, press-scale, hover-lift) so web mirrors mobile.
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, DollarSign, TrendingUp, MapPin, Sun, Clock, Check, Calendar,
  ChevronRight, MessageCircle, Camera, Sprout, Users, X, Droplet, FlaskConical, Scissors, Shrub, Minimize2,
  Snowflake, Layers, ShoppingBag, Map, Zap, RefreshCw, Shield,
} from 'lucide-react';
import { cropImage } from '@/lib/cropImage';

// Mirror of /app/mobile/src/theme colors.
export const C = {
  background: '#FAF9F6', sage: '#E5EBDD', sageDeep: '#D9E1CB', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  forest: '#1A4331', forestDeep: '#0F2D1F', soil: '#7A6B57', leaf: '#6B9F4A', leafDeep: '#4E7A33',
  gold: '#C9A85B', terracotta: '#C66B4A', sky: '#8FB4D6', lime: '#B4D330',
  text: '#2A3528', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF',
  border: 'rgba(26,67,49,0.10)', borderStrong: 'rgba(26,67,49,0.18)',
  goldTint: 'rgba(201,168,91,0.14)', terracottaTint: 'rgba(198,107,74,0.12)',
  skyTint: 'rgba(143,180,214,0.16)', leafTint: 'rgba(107,159,74,0.14)', forestTint: 'rgba(26,67,49,0.06)',
};

const OUTFIT = { fontFamily: 'Outfit, sans-serif' };
const glow = { textShadow: '0 1px 7px rgba(250,246,236,0.95)' };

export const daysTo = (iso) => { if (!iso) return null; return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000); };

// Spring-on-scroll wrapper — matches the native FadeInDown / scroll physics feel.
export function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ type: 'spring', stiffness: 260, damping: 26, delay }}
    >
      {children}
    </motion.div>
  );
}

// Mirror of mobile <Chip/> — pill, icon 12, Manrope 600 12px.
function Chip({ label, tone = 'forest', icon: Icon }) {
  const map = {
    forest: { bg: C.forestTint, fg: C.forest }, gold: { bg: C.goldTint, fg: '#8A6D2B' },
    terracotta: { bg: C.terracottaTint, fg: C.terracotta }, sky: { bg: C.skyTint, fg: '#3F6C97' },
    leaf: { bg: C.leafTint, fg: C.leafDeep }, muted: { bg: C.sageDeep, fg: C.textMuted },
  };
  const c = map[tone];
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-[5px] text-xs font-semibold" style={{ background: c.bg, color: c.fg }}>
      {Icon && <Icon className="w-3 h-3 mr-1" style={{ color: c.fg }} />}
      {label}
    </span>
  );
}

// ── 1. Dispatcher briefing (WhatNowCard) ────────────────────────────────────
export function WhatNowCard({ brief, onGo }) {
  const Icon = brief.cta.icon;
  return (
    <button onClick={onGo} data-testid="whatnow-cta" className="hg-press w-full text-left rounded-[20px] p-6 hg-float" style={{ background: C.forest }}>
      <div className="flex items-center gap-1.5">
        <span className="rounded-full" style={{ width: 7, height: 7, background: C.lime }} />
        <span className="text-[11px] font-bold uppercase tracking-[0.09em]" style={{ color: C.gold }}>{brief.tag}</span>
      </div>
      <h2 data-testid="whatnow-what" className="text-2xl font-semibold mt-1.5 leading-tight" style={{ ...OUTFIT, color: C.cream }}>{brief.what}</h2>
      <p className="text-[15px] font-medium mt-1.5 leading-[22px]" style={{ color: 'rgba(250,246,236,0.82)' }}>{brief.why}</p>
      <div className="flex items-center gap-2 mt-3 rounded-full py-3 px-4" style={{ background: 'rgba(180,211,48,0.16)' }}>
        <Icon className="w-4 h-4" style={{ color: C.cream }} />
        <span className="text-[13px] font-semibold" style={{ color: C.cream }}>{brief.cta.label}</span>
        <ArrowRight className="w-[15px] h-[15px] ml-auto" style={{ color: C.lime }} />
      </div>
    </button>
  );
}

// ── 2. This week's focus (tickable) ─────────────────────────────────────────
const TASK_ICON = { water: Droplet, feed: FlaskConical, thin: Minimize2, stake: Shrub, harvest: Scissors };
const TASK_TONE = { water: '#3E7CB1', feed: '#C9A85B', thin: '#6B9F4A', stake: '#1A4331', harvest: '#C66B4A' };

export function FocusList({ items, onTick, onSkip }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-xl leading-[26px] font-semibold" style={{ ...OUTFIT, color: C.forest, ...glow }}>{t('today.thisWeeksFocus')}</h2>
        <span className="text-[13px] font-semibold" style={{ color: C.textMuted, ...glow }}>{items.length ? 'Tick as you go' : ''}</span>
      </div>
      {items.length === 0 ? (
        <div data-testid="focus-empty" className="mt-3 rounded-[20px] p-4 border flex items-center gap-3" style={{ background: C.white, borderColor: C.border }}>
          <span className="flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: 13, background: C.leaf }}>
            <Check className="w-3.5 h-3.5" style={{ color: C.cream }} />
          </span>
          <p className="text-[15px] font-medium leading-[22px]" style={{ color: C.text }}>You&apos;re all caught up. Enjoy your garden.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 mt-3">
          {items.map((f) => {
            const TypeIcon = f.isTask ? (TASK_ICON[f.taskType] || Sprout) : null;
            const tone = (f.isTask && TASK_TONE[f.taskType]) || C.leaf;
            return (
              <div key={f.key} data-testid={`focus-${f.key}`} className="rounded-[20px] p-4 border flex items-center gap-3" style={{ background: C.white, borderColor: C.border }}>
                <button onClick={() => onTick(f)} data-testid={`focus-tick-${f.key}`} aria-label="Complete focus item"
                  className="hg-press flex-shrink-0 transition-colors" style={{ width: 26, height: 26, borderRadius: 13, border: `2px solid ${C.borderStrong}`, background: C.white }} />
                {TypeIcon ? (
                  <span className="flex items-center justify-center flex-shrink-0" style={{ width: 30, height: 30, borderRadius: 10, background: `${tone}1A` }}>
                    <TypeIcon className="w-4 h-4" style={{ color: tone }} />
                  </span>
                ) : null}
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold leading-[22px]" style={{ color: C.text }}>{f.title}</p>
                  {f.detail ? <p className="text-[13px] leading-[19px] mt-0.5 line-clamp-2" style={{ color: C.textMuted }}>{f.detail}</p> : null}
                </div>
                {f.isTask && onSkip ? (
                  <button onClick={() => onSkip(f)} data-testid={`focus-skip-${f.key}`} aria-label="Skip task" title="Skip"
                    className="hg-press flex-shrink-0 flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 14, background: C.cream }}>
                    <X className="w-4 h-4" style={{ color: C.textMuted }} />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 3. Your growing crops (immersive carousel) ──────────────────────────────
export function CropsStrip({ plantings, onOpen, onSeeAll, onEmpty }) {
  const { t } = useTranslation();
  return (
    <div data-testid="today-crops-strip">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl leading-[26px] font-semibold" style={{ ...OUTFIT, color: C.forest, ...glow }}>{t('today.growingCrops')}</h2>
        {plantings.length > 0 && (
          <button onClick={onSeeAll} data-testid="crops-see-all" className="text-[13px] font-semibold hover:opacity-80" style={{ color: C.leafDeep, ...glow }}>Greenhouse →</button>
        )}
      </div>
      {plantings.length === 0 ? (
        <button onClick={onEmpty} data-testid="today-crops-empty" className="w-full text-left rounded-[20px] p-4 border card-hover flex items-center gap-3 mt-2" style={{ background: C.white, borderColor: C.border }}>
          <div className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 21, background: C.leafTint }}>
            <Sprout className="w-5 h-5" style={{ color: C.leafDeep }} />
          </div>
          <div className="flex-1">
            <p className="text-[15px] font-semibold" style={{ color: C.text }}>Plant your first crop</p>
            <p className="text-[13px]" style={{ color: C.textMuted }}>Browse the handbook and add one to your garden.</p>
          </div>
          <ChevronRight className="w-5 h-5" style={{ color: C.textMuted }} />
        </button>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 pt-3 hg-scroll">
          {plantings.slice(0, 10).map((p, i) => {
            const d = daysTo(p.expected_harvest_date);
            return (
              <motion.button
                key={p.planting_id}
                data-testid={`today-crop-${p.planting_id}`}
                onClick={() => onOpen(p)}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.4), type: 'spring', stiffness: 260, damping: 24 }}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.97 }}
                className="relative flex-shrink-0 overflow-hidden hg-float"
                style={{ width: 148, height: 178, borderRadius: 20, background: C.sageDeep }}
              >
                <img src={cropImage(p.crop_name, p.category)} alt={p.crop_name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(15,45,31,0.04), rgba(15,45,31,0.42), rgba(15,45,31,0.9))' }} />
                <span className="absolute top-2.5 left-2.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-[0.05em]" style={{ background: 'rgba(250,246,236,0.92)', color: C.forest }}>
                  {String(p.stage || '').replace('_', ' ')}
                </span>
                <div className="absolute left-3 right-3 bottom-3 text-left">
                  <p className="text-[15px] font-semibold truncate" style={{ color: C.cream }}>{p.crop_name}</p>
                  <p className="text-[13px]" style={{ color: 'rgba(250,246,236,0.85)' }}>
                    {d == null ? '—' : d <= 0 ? 'Ready to pick' : `~${d}d to harvest`}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 4. This week's value ────────────────────────────────────────────────────
export function GardenValueCard({ weekValue, seasonValue, cur, onOpen }) {
  return (
    <button onClick={onOpen} data-testid="value-open" className="hg-press w-full text-left relative overflow-hidden rounded-[20px] p-4 hg-float" style={{ background: C.creamWarm }}>
      <span className="absolute pointer-events-none" style={{ top: -30, right: -20, width: 140, height: 140, borderRadius: 80, background: C.goldTint }} />
      <div className="relative flex items-center justify-between">
        <span className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 20, background: C.goldTint }}>
          <DollarSign className="w-[18px] h-[18px]" style={{ color: C.gold }} />
        </span>
        <Chip label="This week" tone="gold" icon={TrendingUp} />
      </div>
      <p className="relative text-[11px] font-bold uppercase tracking-[0.09em] mt-3" style={{ color: '#8A6D2B' }}>Value grown at home</p>
      <p data-testid="value-week" className="relative font-semibold" style={{ ...OUTFIT, fontSize: 40, lineHeight: '46px', color: C.forest }}>
        {weekValue ? `${cur}${Math.round(weekValue.low)}–${cur}${Math.round(weekValue.high)}` : `${cur}0`}
      </p>
      {seasonValue ? (
        <div className="relative flex items-center gap-1.5 mt-2">
          <Sprout className="w-3.5 h-3.5" style={{ color: C.leafDeep }} />
          <span className="text-[13px]" style={{ color: C.leafDeep }}>{`${cur}${Math.round(seasonValue.low)}–${cur}${Math.round(seasonValue.high)} this season`}</span>
        </div>
      ) : (
        <p className="relative text-[13px] mt-1" style={{ color: C.textMuted }}>Log a harvest to grow this number.</p>
      )}
    </button>
  );
}

// ── 5. What to sow in {month} ───────────────────────────────────────────────
export function SowPlanner({ planner }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const sowNow = planner?.sow_now || [];
  const comingUp = planner?.coming_up || [];
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="inline-block rounded-2xl px-3 py-[5px] hg-float" style={{ background: C.creamWarm }}>
          <span className="text-xl leading-[26px] font-semibold" style={{ ...OUTFIT, color: C.forest }}>{t('today.whatToSow')} · {planner?.current_month}</span>
        </span>
        <button onClick={() => navigate('/season-plan')} data-testid="sow-see-all" className="rounded-2xl px-3 py-[5px] hg-float text-[13px] font-semibold" style={{ background: C.creamWarm, color: C.forest }}>Planner →</button>
      </div>
      {planner?.zone_label ? (
        <div className="flex mt-2">
          <span className="inline-flex items-center rounded-full px-3 py-1.5 border max-w-full" style={{ background: C.cream, borderColor: C.border }}>
            <MapPin className="w-3 h-3 mr-1.5 flex-shrink-0" style={{ color: C.leafDeep }} />
            <span className="text-[13px] font-semibold truncate" style={{ color: C.leafDeep }}>{`${planner.zone_label}${planner.location ? ` · ${planner.location}` : ''}`}</span>
          </span>
        </div>
      ) : null}
      {sowNow.length ? (
        <div className="flex gap-2 overflow-x-auto pb-2 pt-3 hg-scroll">
          {sowNow.slice(0, 12).map((s) => (
            <motion.button
              key={s.crop_id}
              data-testid={`sow-crop-${s.crop_id}`}
              onClick={() => navigate(`/crops/${s.crop_id}`)}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              className="flex-shrink-0 text-left rounded-2xl p-3 border hg-float"
              style={{ width: 210, background: s.closing_soon ? '#FBEDE7' : C.white, borderColor: s.closing_soon ? 'rgba(198,107,74,0.45)' : C.border }}
            >
              <div className="flex items-center gap-2">
                <img src={cropImage(s.name)} alt={s.name} className="flex-shrink-0 object-cover" style={{ width: 42, height: 42, borderRadius: 12, background: C.sageDeep }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-semibold truncate" style={{ color: C.text }}>{s.name}</p>
                  <p className="text-[13px] truncate" style={{ color: C.textMuted }}>
                    {s.transplant_now && !s.sow_now ? 'Transplant now' : 'Sow now'}{s.weeks_to_maturity ? ` · ~${s.weeks_to_maturity}wk` : ''}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {s.closing_soon ? <Chip label="Window closing" tone="terracotta" icon={Clock} /> : <Chip label={s.sow_now ? 'Sow' : 'Transplant'} tone="leaf" icon={Sun} />}
                {s.growing ? <Chip label="Growing" tone="forest" icon={Check} /> : null}
              </div>
            </motion.button>
          ))}
        </div>
      ) : null}
      {comingUp.length ? (
        <div data-testid="sow-coming-up" className="flex items-center gap-2 mt-3 rounded-2xl p-3 hg-float" style={{ background: C.forest }}>
          <Calendar className="w-[15px] h-[15px] flex-shrink-0" style={{ color: C.lime }} />
          <p className="text-[13px] leading-[19px] line-clamp-2" style={{ color: C.cream }}>
            <span className="font-semibold" style={{ color: C.lime }}>Coming up in {planner?.next_month}: </span>
            {comingUp.slice(0, 4).map((c) => c.name).join(', ')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ── 6. Growing near you ─────────────────────────────────────────────────────
export function CommunityPeek({ nearYou, onOpen }) {
  const { t } = useTranslation();
  return (
    <button onClick={onOpen} data-testid="community-peek" className="hg-press w-full text-left rounded-[20px] p-4 border" style={{ background: C.white, borderColor: C.border }}>
      <div className="flex items-center justify-between">
        <span className="text-[17px] leading-[23px] font-semibold" style={{ ...OUTFIT, color: C.forest }}>{t('today.growingNearYou')}</span>
        <span className="flex items-center gap-1">
          <span className="text-[13px] font-semibold" style={{ color: C.leafDeep }}>{t('nav.community')}</span>
          <ChevronRight className="w-4 h-4" style={{ color: C.leafDeep }} />
        </span>
      </div>
      <p className="text-[13px] mt-0.5 mb-2" style={{ color: C.textMuted }}>{nearYou.region}</p>
      <div className="flex flex-wrap gap-2">
        {nearYou.trending_crops.slice(0, 6).map((c) => (
          <Chip key={c.name} label={`${c.name} · ${c.count}`} tone="leaf" />
        ))}
      </div>
    </button>
  );
}

// ── 7. Daily tip ────────────────────────────────────────────────────────────
export function DailyTip({ tip }) {
  const { t } = useTranslation();
  return (
    <div data-testid="daily-tip" className="rounded-[20px] p-4" style={{ background: C.creamWarm }}>
      <div className="flex items-center gap-1">
        <Sun className="w-4 h-4" style={{ color: C.gold }} />
        <span className="text-[11px] font-bold uppercase tracking-[0.09em]" style={{ color: C.gold }}>{t('today.dailyTip')}</span>
      </div>
      <p className="text-[15px] font-medium leading-[22px] mt-1.5" style={{ color: C.text }}>{tip.text}</p>
    </div>
  );
}

// ── 8. Quick action pills ───────────────────────────────────────────────────
function ActionPill({ icon: Icon, label, sub, onPress, primary, testID }) {
  return (
    <motion.button whileTap={{ scale: 0.98 }} onClick={onPress} data-testid={testID}
      className="hg-press w-full flex items-center gap-3 rounded-full py-3 px-3 hg-float"
      style={{ background: primary ? C.forest : C.white }}>
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 20, background: primary ? 'rgba(180,211,48,0.22)' : C.forestTint }}>
        <Icon className="w-[18px] h-[18px]" style={{ color: primary ? C.lime : C.forest }} />
      </span>
      <span className="flex-1 min-w-0 text-left">
        <span className="block text-[15px] font-semibold" style={{ color: primary ? C.cream : C.forest }}>{label}</span>
        <span className="block text-[13px] truncate" style={{ color: primary ? 'rgba(250,246,236,0.75)' : C.textMuted }}>{sub}</span>
      </span>
      <ArrowRight className="w-[18px] h-[18px] flex-shrink-0" style={{ color: primary ? C.lime : C.textMuted }} />
    </motion.button>
  );
}

export function ActionPills({ aiOn, onCoach, onScan }) {
  return (
    <div className="flex flex-col gap-2">
      {aiOn ? <ActionPill testID="pill-coach" primary icon={MessageCircle} label="Ask the Coach" sub="Your master grower, on tap" onPress={onCoach} /> : null}
      <ActionPill testID="pill-scanner" icon={Camera} label="Scan a pest or disease" sub="Snap a leaf for a diagnosis" onPress={onScan} />
    </div>
  );
}

export { Users };

// 8 · Winter / off-season mode (P1.5) — a reason to open in July.
const WINTER_ICONS = {
  layers: Layers, sprout: Sprout, 'refresh-cw': RefreshCw, shield: Shield, droplet: Droplet,
  map: Map, 'shopping-bag': ShoppingBag, calendar: Calendar, zap: Zap,
};

export function WinterModeCard({ data, onCrop, onGo }) {
  if (!data || !data.active) return null;
  const Icon = ({ name, color }) => {
    const Cmp = WINTER_ICONS[name] || Sprout;
    return <Cmp className="w-4 h-4" style={{ color }} />;
  };
  return (
    <div data-testid="winter-mode-card" className="rounded-3xl p-5"
      style={{ background: 'linear-gradient(160deg, #0F2D1F 0%, #1A4331 60%, #24543E 100%)', boxShadow: '0 20px 50px -24px rgba(15,45,31,0.7)' }}>
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(180,211,48,0.16)' }}>
          <Snowflake className="w-5 h-5" style={{ color: C.lime }} />
        </div>
        <div>
          <p className="text-base font-bold text-white" style={OUTFIT} data-testid="winter-mode-title">Winter Mode · {data.season_label}</p>
          {data.zone_label && <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{data.zone_label}</p>}
        </div>
      </div>
      <p className="text-[13px] leading-snug mb-4" style={{ color: 'rgba(255,255,255,0.8)' }}>{data.intro}</p>

      {data.overwinter_crops?.length > 0 && (
        <div className="mb-4" data-testid="winter-overwinter">
          <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: C.lime }}>Plant now</p>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
            {data.overwinter_crops.map((c) => (
              <button key={c.crop_id} data-testid={`winter-crop-${c.crop_id}`} onClick={() => onCrop && onCrop(c.crop_id)}
                className="flex-shrink-0 text-left rounded-2xl px-3 py-2 hover:brightness-110 transition" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
                <p className="text-[13px] font-semibold text-white whitespace-nowrap">{c.name}</p>
                <p className="text-[10px]" style={{ color: C.lime }}>{c.action}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4" data-testid="winter-soil-prep">
        <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: C.lime }}>Build the soil</p>
        <div className="space-y-2">
          {data.soil_prep?.map((t, i) => (
            <div key={i} className="flex items-start gap-2.5 rounded-2xl p-2.5" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(180,211,48,0.14)' }}><Icon name={t.icon} color={C.lime} /></div>
              <div>
                <p className="text-[13px] font-semibold text-white">{t.title}</p>
                <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.68)' }}>{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div data-testid="winter-planning">
        <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: C.lime }}>Get ahead for spring</p>
        <div className="space-y-2">
          {data.planning?.map((p, i) => (
            <button key={i} onClick={() => p.action_url && onGo && onGo(p.action_url)} disabled={!p.action_url}
              data-testid={`winter-plan-${i}`}
              className={`w-full flex items-center gap-2.5 rounded-2xl p-2.5 text-left transition ${p.action_url ? 'hover:brightness-110' : ''}`}
              style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(180,211,48,0.14)' }}><Icon name={p.icon} color={C.lime} /></div>
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-white">{p.title}</p>
                <p className="text-[11px] leading-snug" style={{ color: 'rgba(255,255,255,0.68)' }}>{p.body}</p>
              </div>
              {p.action_url && <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
